// Libraries
import React, { useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { FullStory, init } from '@fullstory/browser';
//import gtag from 'ga-gtag';

// Services
import userService from '../services/userService.js';
import paymentService from '../services/paymentService.js';
import { SESSION_OUT_OF_SYNC_EVENT } from './Modals/SessionOutOfSyncModal.js';

// Import Context
import { UserContext } from '../context/User';

// Construct Component
const UserManager = ({ callRender }) => {
  const { user, setUser, updateUser, setUpdateUser } = useContext(UserContext);
  const { isLoading, getAccessTokenSilently } = useAuth0();
  // `user` can be truthy on mount from a stale localStorage cache, before the
  // token has actually been (re)validated -- track the one real post-login
  // refresh separately so it isn't skipped just because `user.id` was already
  // set, and so it can't loop (setUser below would otherwise re-trigger this
  // effect via the `user` dependency).
  const hasRefreshedUserRef = useRef(false);

  async function fetchToken() {
    const valid = (await userService.validateToken()) || (await refreshToken());
    return valid;
  }

  async function refreshToken() {
    try {
      const accessToken = await getAccessTokenSilently({ grant: 'refresh_token' });
      return await userService.validateToken(accessToken);
    } catch {
      return false;
    }
  }

  const fullUserDataLoad = async () => {
    let newData = {};
    const userData = await userService.getUserData();
    if (userData) {
      let isStaff = await userService.validateStaffToken();
      isStaff = isStaff ? true : false;

      // /availableCards returns { paymentMethods, defaultPaymentMethodId } --
      // or a bare [] when the user has no Stripe customer yet. Consumers
      // expect user.cards to be the list itself.
      const cardResp = await paymentService.getAvailableCards(userData.id);
      newData = {
        ...userData,
        isStaff: isStaff,
        cards: cardResp?.paymentMethods ?? (Array.isArray(cardResp) ? cardResp : []),
        defaultPaymentMethodId: cardResp?.defaultPaymentMethodId ?? null,
        invoiceDue: await paymentService.checkForPaymentsDue(userData.id),
      };
      setUser(newData);
    }
    return newData;
  };

  const handleUserUpdate = async (updateType) => {
    let update = updateType || updateUser;
    async function updateSubscription() {
      let newUserData = await fullUserDataLoad();
      setUpdateUser('');
    }

    async function updateBasic() {
      const userData = await userService.getUserData();
      if (userData && userData.id) {
        let updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        setUpdateUser('');
      }
    }

    if (update === 'subscription') {
      updateSubscription();
    } else if (update === 'basic') {
      updateBasic();
    }
  };

  useEffect(() => {
    async function initializeUser() {
      if (isLoading) return;
      const hadStoredUser = !!localStorage.getItem('user');
      const tokenValid = await fetchToken();

      if (!tokenValid && hadStoredUser) {
        window.dispatchEvent(new CustomEvent(SESSION_OUT_OF_SYNC_EVENT));
        return;
      }

      if (tokenValid && !hasRefreshedUserRef.current) {
        hasRefreshedUserRef.current = true;
        let userData = await fullUserDataLoad();
        window.usetifulTags = { userId: userData.id };
        init({ orgId: 'o-1STRV8-na1' });
        FullStory('setIdentity', { uid: userData.id, properties: userData });
        /*
        gtag('event', 'user_login', {
          user_id: userData.id,
        });
        */
      }
    }
    initializeUser();

    return () => {
      // Cleanup logic if needed
    };
  }, [user, setUser, isLoading]);

  useEffect(() => {
    // For a logged-out visitor on an /app/ path, `user` never becomes
    // truthy, so without the `!isLoading` fallback this never fires and the
    // page holder stays blank forever -- HeaderBar's own redirect (also
    // gated on `isLoading`) sends them to /accessRequired around the same
    // time, so by the time routes render, the pathname already reflects it.
    if ((user && user.id) || !isLoading || !window.location.pathname.includes('/app/')) {
      callRender(true);
    }
  }, [user, isLoading]);

  useEffect(() => {
    handleUserUpdate();
  }, [updateUser]);

  useEffect(() => {
    handleUserUpdate('basic');
  }, [window.location.pathname]);

  return <></>;
};

export default UserManager;
